// "Shipped in A2000" (Part A): a line's pick tickets that the WMS has SHIPPED or BILLED, from the
// dataset `routing-pick-ticket-shipments`. A flag only: nothing moves by itself — the team still
// marks the line shipped. The values are A2000's, as they are.

import { formatShortDay } from "./days";
import { readBaseline } from "./integrity";
import { ptRanges } from "./pt-text";
import type { LinePickTicketRecord, PtShipmentRow, RoutingLineRecord, ShippingRowRecord } from "./types";

/** The WMS statuses that mean the pick ticket left. */
export const A2000_SHIPPED_STATUSES = ["SHIPPED", "BILLED"] as const;

export const isShippedInA2000 = (r: Pick<PtShipmentRow, "wms_status">) =>
  (A2000_SHIPPED_STATUSES as readonly string[]).includes(String(r.wms_status ?? "").toUpperCase());

/** What A2000 says of a line's pick tickets: those shipped, of how many. */
export interface A2000Shipped {
  /** The shipped pick tickets' rows, by pick ticket. */
  shipped: PtShipmentRow[];
  /** Pick tickets on the line. */
  of: number;
  /** Every pick ticket shipped. */
  all: boolean;
  /** The latest date shipped; null when A2000 has none (some SHIPPED rows). */
  latest: string | null;
}

/** The flag of a line's pick tickets; null when none is shipped in A2000. */
export function a2000Shipped(
  pts: readonly { pickTicket: string }[],
  byPt: ReadonlyMap<string, PtShipmentRow>,
): A2000Shipped | null {
  const shipped = pts
    .map((p) => byPt.get(p.pickTicket))
    .filter((r): r is PtShipmentRow => !!r && isShippedInA2000(r))
    .sort((a, b) => a.pick_ticket - b.pick_ticket);
  if (!shipped.length) return null;
  const latest =
    shipped
      .map((r) => r.date_shipped)
      .filter((d): d is string => !!d)
      .sort()
      .at(-1) ?? null;
  return { shipped, of: pts.length, all: shipped.length === pts.length, latest };
}

/**
 * The chip: "Shipped in A2000 · 10/19", or "2 of 5 PTs shipped in A2000". With no date shipped
 * (some SHIPPED and BILLED rows have none), the status: "Shipped in A2000 · BILLED".
 */
export function a2000Text(a: A2000Shipped): string {
  if (!a.all) return `${a.shipped.length} of ${a.of} PTs shipped in A2000`;
  if (a.latest) return `Shipped in A2000 · ${formatShortDay(a.latest)}`;
  const statuses = [...new Set(a.shipped.map((r) => String(r.wms_status ?? "").toUpperCase()))].sort();
  return `Shipped in A2000 · ${statuses.join(" / ")}`;
}

/** The chip's tooltip, one line per shipped pick ticket: "PT 1807901 · BILLED · 10/19 · FEDEX FREIGHT · BOL 123". */
export function a2000Lines(a: A2000Shipped): string[] {
  // Pick tickets that share all four are one line: "PT 1793968–1793979 (12) · SHIPPED · …".
  const groups = new Map<string, { pts: string[]; rest: string }>();
  for (const r of a.shipped) {
    const rest = [
      String(r.wms_status ?? "").toUpperCase(),
      r.date_shipped ? formatShortDay(r.date_shipped) : "no date",
      r.carrier_name || r.ship_via || "",
      r.bol_no ? `BOL ${r.bol_no}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    const g = groups.get(rest) ?? { pts: [], rest };
    g.pts.push(String(r.pick_ticket));
    groups.set(rest, g);
  }
  return [...groups.values()].map(({ pts, rest }) => {
    const ranges = ptRanges(pts).replace(/-/g, "–");
    return `PT ${ranges}${pts.length > 1 ? ` (${pts.length})` : ""} · ${rest}`;
  });
}

/**
 * The pick tickets whose A2000 shipment the pages need beyond the open view's: those held by an
 * open line or a line on a scheduled pickup, but no longer open in A2000 — a pick ticket leaves
 * the open view once A2000 ships it. The open ones are read with the open view (`source.ts`).
 */
export function heldPickTickets(input: {
  lines: readonly RoutingLineRecord[];
  pickTickets: readonly LinePickTicketRecord[];
  shippingRows: readonly ShippingRowRecord[];
  open: ReadonlySet<string>;
}): string[] {
  const scheduled = new Set(input.shippingRows.filter((r) => r.status === "On pickup").map((r) => r.lineId));
  const wanted = new Set(input.lines.filter((l) => !l.finishedStatus || scheduled.has(l.id)).map((l) => l.id));
  const out = new Set<string>();
  const keep = (pt: string) => !input.open.has(pt) && /^\d{1,10}$/.test(pt) && out.add(pt);
  for (const l of input.lines) if (wanted.has(l.id)) for (const c of readBaseline(l).copies) keep(c.pickTicket);
  for (const c of input.pickTickets) if (wanted.has(c.lineId)) keep(String(c.pickTicket));
  return [...out].sort();
}

/** The flag as a filter value, the grid's and the reports' alike. */
export const A2000_VALUES = ["All", "Some", "No"] as const;
export const a2000Value = (a: A2000Shipped | null): (typeof A2000_VALUES)[number] =>
  a ? (a.all ? "All" : "Some") : "No";
